import { useCallback, useMemo, useRef, useState } from 'react';

type AnimatedToggler = {
    on: () => void;
    off: () => void;
};

export const useAnimatedToggle = (
    duration: number
): [isOpen: boolean, isClosing: boolean, toggler: AnimatedToggler] => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const stateRef = useRef({ isOpen: false, isClosing: false });

    stateRef.current.isOpen = isOpen;
    stateRef.current.isClosing = isClosing;

    const on = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsClosing(false);
        setIsOpen(true);
    }, []);

    const off = useCallback(() => {
        if (!stateRef.current.isOpen || stateRef.current.isClosing) return;
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            closeTimeoutRef.current = null;
        }, duration);
    }, [duration]);

    const api = useMemo(() => ({ on, off }), [on, off]);

    return [isOpen, isClosing, api];
};
